import {defineStore} from 'pinia';
import {computed, ref} from "vue";
import type {Ref} from "vue";
import type {AxiosRequestConfig, AxiosResponse} from "axios";
import {api} from "../scripts/api";
import type {PageResponse} from "../../responses/PageResponse.ts";
import type {ProductResponse} from "../../responses/ProductResponse.ts";
import type {ArticleResponse} from "../../responses/ArticleResponse.ts";
import type {ProjectResponse} from "../../responses/ProjectResponse";

interface SearchResult<T> {
    count: number
    items: T[]
}

interface SearchResults {
    pages?: SearchResult<PageResponse>
    products?: SearchResult<ProductResponse>
    articles?: SearchResult<ArticleResponse>
    projects?: SearchResult<ProjectResponse>
}

export const useSearchStore = defineStore('search', () => {
    const results: Ref<SearchResults|null> = ref(null);
    const isLoading: Ref<boolean> = ref(false);

    const totalResults = computed(() => {
        if (results.value === null) return 0

        let count = 0;

        for (const [key, value] of Object.entries(results.value)) {
            count += value.count;
        }

        return count;
    })

    let abortController: AbortController | null = null;

    const getSearchResults = (query: string, type: string) => {
        if (query.length > 1) {
            isLoading.value = true

            return new Promise(async (resolve, reject): Promise<void> => {
                if (abortController != null) {
                    abortController.abort();
                }

                abortController = new AbortController()
                try {
                    const response: AxiosResponse<SearchResults> = await api().post(
                        `/site/search/`,
                        {
                            query: query,
                            type: type
                        },
                        {signal: abortController.signal} as AxiosRequestConfig,
                    )

                    results.value = response.data;
                    resolve(response.data);
                } catch (e) {
                    reject(e)
                } finally {
                    setTimeout(() => {
                        isLoading.value = false
                    }, 250)
                }
            });
        } else {
            results.value = null
        }
    }

    return {
        isLoading,
        results,
        totalResults,
        getSearchResults,
    }
});
